import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/store/authStore'

class SocketService {
  private socket: Socket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  connect(token: string): void {
    if (this.socket?.connected) {
      return
    }

    this.socket = io(import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000', {
      auth: { token },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true,
    })

    this.setupEventListeners()
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return

    // Connection events
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0
    })

    this.socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        this.socket?.connect()
      }
    })

    this.socket.on('connect_error', () => {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++
        setTimeout(() => {
          this.socket?.connect()
        }, this.reconnectDelay * this.reconnectAttempts)
      }
    })

    // Authentication events
    this.socket.on('auth_error', () => {
      useAuthStore.getState().logout()
    })

    // Custom events
    this.socket.on('complaint_updated', (data) => {
      this.handleComplaintUpdate(data)
    })

    this.socket.on('complaint_assigned', (data) => {
      this.handleComplaintAssignment(data)
    })

    this.socket.on('status_change', (data) => {
      this.handleStatusChange(data)
    })

    this.socket.on('notification', (data) => {
      this.handleNotification(data)
    })

    this.socket.on('department_update', (data) => {
      this.handleDepartmentUpdate(data)
    })

    this.socket.on('user_update', (data) => {
      this.handleUserUpdate(data)
    })

    this.socket.on('event_update', (data) => {
      this.handleEventUpdate(data)
    })

    this.socket.on('charge_update', (data) => {
      this.handleChargeUpdate(data)
    })
  }

  // Join rooms
  joinComplaint(complaintId: string): void {
    this.socket?.emit('join_complaint', complaintId)
  }

  leaveComplaint(complaintId: string): void {
    this.socket?.emit('leave_complaint', complaintId)
  }

  joinDepartment(departmentId: string): void {
    this.socket?.emit('join_department', departmentId)
  }

  // Send events
  sendComplaintUpdate(complaintId: string, update: any): void {
    this.socket?.emit('complaint_update', { complaintId, update })
  }

  sendStatusChange(complaintId: string, status: string, notes?: string): void {
    this.socket?.emit('status_change', { complaintId, status, notes })
  }

  sendAssignment(complaintId: string, assignedTo: string): void {
    this.socket?.emit('complaint_assignment', { complaintId, assignedTo })
  }

  sendComment(complaintId: string, comment: string, isInternal: boolean = false): void {
    this.socket?.emit('complaint_comment', { complaintId, comment, isInternal })
  }

  // Event handlers
  private handleComplaintUpdate(data: any): void {
    // Emit custom event for components to listen to
    window.dispatchEvent(new CustomEvent('complaint_updated', { detail: data }))
  }

  private handleComplaintAssignment(data: any): void {
    window.dispatchEvent(new CustomEvent('complaint_assigned', { detail: data }))
  }

  private handleStatusChange(data: any): void {
    window.dispatchEvent(new CustomEvent('status_changed', { detail: data }))
  }

  private handleNotification(data: any): void {
    window.dispatchEvent(new CustomEvent('notification_received', { detail: data }))
  }

  private handleDepartmentUpdate(data: any): void {
    window.dispatchEvent(new CustomEvent('department_updated', { detail: data }))
  }

  private handleUserUpdate(data: any): void {
    window.dispatchEvent(new CustomEvent('user_updated', { detail: data }))
  }

  private handleEventUpdate(data: any): void {
    window.dispatchEvent(new CustomEvent('event_updated', { detail: data }))
  }

  private handleChargeUpdate(data: any): void {
    window.dispatchEvent(new CustomEvent('charge_updated', { detail: data }))
  }

  // Utility methods
  isConnected(): boolean {
    return this.socket?.connected || false
  }

  getSocketId(): string | undefined {
    return this.socket?.id
  }

  // Reconnection
  reconnect(): void {
    if (this.socket) {
      this.socket.connect()
    }
  }

  // Manual event emission
  emit(event: string, data: any): void {
    this.socket?.emit(event, data)
  }

  // Manual event listening
  on(event: string, callback: (data: any) => void): void {
    this.socket?.on(event, callback)
  }

  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.socket?.off(event, callback)
    } else {
      this.socket?.off(event)
    }
  }
}

// Create singleton instance
export const socketService = new SocketService()

// Export socket instance for direct use
export const socket = socketService

// Hook for using socket in components
export const useSocket = () => {
  return {
    socket: socketService,
    isConnected: socketService.isConnected(),
    connect: (token: string) => socketService.connect(token),
    disconnect: () => socketService.disconnect(),
    joinComplaint: (complaintId: string) => socketService.joinComplaint(complaintId),
    leaveComplaint: (complaintId: string) => socketService.leaveComplaint(complaintId),
    joinDepartment: (departmentId: string) => socketService.joinDepartment(departmentId),
    sendComplaintUpdate: (complaintId: string, update: any) => socketService.sendComplaintUpdate(complaintId, update),
    sendStatusChange: (complaintId: string, status: string, notes?: string) => socketService.sendStatusChange(complaintId, status, notes),
    sendAssignment: (complaintId: string, assignedTo: string) => socketService.sendAssignment(complaintId, assignedTo),
    sendComment: (complaintId: string, comment: string, isInternal?: boolean) => socketService.sendComment(complaintId, comment, isInternal),
  }
}
